/**
 * Temporizador de enfoque. El estado guarda instantes, no un contador: el tiempo se
 * calcula con Date.now(), así que sobrevive a recargas, a pestañas suspendidas y a que
 * el navegador detenga los temporizadores de JavaScript en segundo plano.
 */
import type { LocalDate } from '@habitos/engine';
import { write } from '../db/repo';
import { getMeta, setMeta } from '../db/db';
import { uuidv7 } from '../lib/ids';

export const FOCUS_PRESETS = [15, 25, 50] as const;
export const FOCUS_MIN_MINUTES = 1;
export const FOCUS_MAX_MINUTES = 180;
const META_KEY = 'focusSession';

export interface FocusState {
  plannedSeconds: number;
  habitId: string | null;
  label: string | null;
  /** Inicio de la sesión (ms). */
  startedAt: number;
  /** Desde cuándo corre el tramo actual; null si está en pausa. */
  runningSince: number | null;
  /** Tiempo acumulado en tramos anteriores (ms). */
  accumulatedMs: number;
}

export function startFocus(minutes: number, habitId: string | null, label: string | null, now: number): FocusState {
  const m = Math.round(minutes);
  if (!Number.isFinite(m) || m < FOCUS_MIN_MINUTES || m > FOCUS_MAX_MINUTES) throw new RangeError('focus_duration');
  return { plannedSeconds: m * 60, habitId, label: label?.trim() || null, startedAt: now, runningSince: now, accumulatedMs: 0 };
}

/** Si el reloj del sistema retrocede, el tramo actual no resta tiempo. */
export function elapsedMs(s: FocusState, now: number): number {
  return s.accumulatedMs + (s.runningSince !== null ? Math.max(0, now - s.runningSince) : 0);
}

export function remainingMs(s: FocusState, now: number): number {
  return Math.max(0, s.plannedSeconds * 1000 - elapsedMs(s, now));
}

export function isComplete(s: FocusState, now: number): boolean {
  return elapsedMs(s, now) >= s.plannedSeconds * 1000;
}

export function pauseFocus(s: FocusState, now: number): FocusState {
  if (s.runningSince === null) return s;
  return { ...s, accumulatedMs: elapsedMs(s, now), runningSince: null };
}

export function resumeFocus(s: FocusState, now: number): FocusState {
  if (s.runningSince !== null) return s;
  return { ...s, runningSince: now };
}

/**
 * Datos del registro al terminar. El tiempo activo nunca supera lo planificado: si la
 * pestaña estuvo cerrada más allá del final, no se cuentan minutos que no se vieron.
 */
export function focusRecord(s: FocusState, now: number, logicalDate: LocalDate) {
  const activeSeconds = Math.min(s.plannedSeconds, Math.round(elapsedMs(s, now) / 1000));
  return {
    habitId: s.habitId,
    label: s.label,
    plannedSeconds: s.plannedSeconds,
    activeSeconds,
    startedAt: new Date(s.startedAt).toISOString(),
    endedAt: new Date(now).toISOString(),
    logicalDate,
    completed: activeSeconds >= s.plannedSeconds,
    deletedAt: null,
  };
}

export async function loadFocus(): Promise<FocusState | null> {
  return getMeta<FocusState | null>(META_KEY, null);
}

export async function saveFocus(s: FocusState | null): Promise<void> {
  await setMeta(META_KEY, s);
}

/** Guarda la sesión y limpia el estado. Devuelve el id del registro (para deshacer). */
export async function finishFocus(s: FocusState, now: number, logicalDate: LocalDate): Promise<{ id: string; activeSeconds: number }> {
  const id = uuidv7();
  const data = focusRecord(s, now, logicalDate);
  await write('focus', id, data);
  await saveFocus(null);
  return { id, activeSeconds: data.activeSeconds };
}

export async function deleteFocusRecord(id: string): Promise<void> {
  await write('focus', id, { deletedAt: new Date().toISOString() });
}

export { focusStats, type FocusStats } from '@habitos/assistant/domain/focusStats';
